import { type FC, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useCreateRestreamDestinationMutation } from "@/partner/entities/streams";

interface CreateRestreamDestinationDialogProps {
  contentId: string;
  open: boolean;
  onClose: () => void;
}

export const CreateRestreamDestinationDialog: FC<
  CreateRestreamDestinationDialogProps
> = ({ contentId, open, onClose }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [streamKey, setStreamKey] = useState("");

  const [createDestination, { loading }] = useCreateRestreamDestinationMutation(
    {
      refetchQueries: ["Stream"],
      onCompleted: () => {
        setTitle("");
        setUrl("");
        setStreamKey("");
        onClose();
      },
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    }
  );

  const handleSubmit = () => {
    void createDestination({
      variables: { contentId, input: { title, url, streamKey } },
    });
  };

  const isValid = title.trim() && url.trim() && streamKey.trim();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("streams.form.restream.createDialogTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            fullWidth
            required
            label={t("streams.form.restream.titleField")}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <TextField
            fullWidth
            required
            label={t("streams.form.restream.urlField")}
            placeholder="rtmps://..."
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
          <TextField
            fullWidth
            required
            label={t("streams.form.restream.streamKeyField")}
            value={streamKey}
            onChange={(event) => setStreamKey(event.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.actions.cancel")}</Button>
        <Button
          variant="contained"
          disabled={!isValid || loading}
          onClick={handleSubmit}
        >
          {t("common.actions.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
